interface OrderItem {
  title: string;
  variantTitle: string | null;
  quantity: number;
  price: string;
}

interface TrackingInfo {
  company: string;
  number: string;
  url: string | null;
}

interface ShippingAddress {
  name: string;
  address1: string;
  city: string;
  province: string;
  zip: string;
  country: string;
}

export interface OrderData {
  id: number;
  orderNumber: string;
  date: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  financialStatus: string;
  fulfillmentStatus: string | null;
  total: string;
  subtotal: string;
  totalTax: string;
  totalDiscounts: string;
  currency: string;
  items: OrderItem[];
  shippingAddress: ShippingAddress | null;
  tracking: TrackingInfo[];
}

interface Props {
  order: OrderData;
  onReset: () => void;
}

type StepStatus = "done" | "active" | "pending";

interface TimelineStep {
  label: string;
  sublabel: string;
  icon: string;
  status: StepStatus;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(amount: string, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(parseFloat(amount));
}

function financialLabel(status: string) {
  const map: Record<string, string> = {
    pending: "Payment Pending",
    authorized: "Payment Authorized",
    partially_paid: "Partially Paid",
    paid: "Paid",
    partially_refunded: "Partially Refunded",
    refunded: "Refunded",
    voided: "Voided",
  };
  return map[status] || status;
}

function buildTimeline(order: OrderData): TimelineStep[] {
  const isCancelled = !!order.cancelledAt;
  const isPaid = ["paid", "partially_paid", "authorized"].includes(order.financialStatus);
  const isShipped = order.fulfillmentStatus === "fulfilled" || order.fulfillmentStatus === "partial";
  const hasTracking = order.tracking.length > 0;

  if (isCancelled) {
    return [
      { label: "Order Placed", sublabel: formatDate(order.date), icon: "ri-shopping-bag-3-line", status: "done" },
      { label: "Order Cancelled", sublabel: order.cancelReason ? `Reason: ${order.cancelReason.replace(/_/g, " ")}` : "Cancelled by customer or store", icon: "ri-close-circle-line", status: "active" },
    ];
  }

  return [
    {
      label: "Order Placed",
      sublabel: formatDate(order.date),
      icon: "ri-shopping-bag-3-line",
      status: "done",
    },
    {
      label: "Payment Confirmed",
      sublabel: isPaid ? financialLabel(order.financialStatus) : "Awaiting payment",
      icon: "ri-secure-payment-line",
      status: isPaid ? "done" : "active",
    },
    {
      label: "Processing",
      sublabel: isShipped ? "Prepared and handed to carrier" : isPaid ? "Your order is being prepared" : "Waiting on payment",
      icon: "ri-box-3-line",
      status: isShipped ? "done" : isPaid ? "active" : "pending",
    },
    {
      label: "Shipped",
      sublabel: isShipped
        ? hasTracking
          ? `Tracking: ${order.tracking[0].number}`
          : "Shipped — tracking info coming soon"
        : "Not yet shipped",
      icon: "ri-truck-line",
      status: isShipped ? "done" : "pending",
    },
    {
      label: "Delivered",
      sublabel: isShipped ? "Expected at your door soon" : "Pending shipment",
      icon: "ri-home-smile-line",
      status: "pending",
    },
  ];
}

function TimelineStep({ step, isLast }: { step: TimelineStep; isLast: boolean }) {
  const colors = {
    done: {
      ring: "border-emerald-500 bg-emerald-500",
      icon: "text-white",
      label: "text-gray-900",
      sub: "text-gray-500",
      line: "bg-emerald-400",
    },
    active: {
      ring: "border-amber-500 bg-amber-50",
      icon: "text-amber-500",
      label: "text-gray-900 font-bold",
      sub: "text-amber-600",
      line: "bg-gray-200",
    },
    pending: {
      ring: "border-gray-200 bg-white",
      icon: "text-gray-300",
      label: "text-gray-400",
      sub: "text-gray-300",
      line: "bg-gray-200",
    },
  };
  const c = colors[step.status];

  return (
    <div className="flex gap-4">
      {/* Icon column */}
      <div className="flex flex-col items-center">
        <div className={`w-9 h-9 flex items-center justify-center rounded-full border-2 shrink-0 transition-all ${c.ring}`}>
          <i className={`${step.icon} text-base ${c.icon}`}></i>
        </div>
        {!isLast && <div className={`w-0.5 flex-1 mt-1 mb-1 rounded-full ${c.line}`}></div>}
      </div>

      {/* Text column */}
      <div className={`pb-6 ${isLast ? "" : ""}`}>
        <p className={`text-sm font-semibold ${c.label}`}>{step.label}</p>
        <p className={`text-xs mt-0.5 ${c.sub}`}>{step.sublabel}</p>
      </div>
    </div>
  );
}

export default function OrderResult({ order, onReset }: Props) {
  const timeline = buildTimeline(order);
  const isCancelled = !!order.cancelledAt;

  const statusBadge = isCancelled
    ? { label: "Cancelled", cls: "bg-red-100 text-red-700" }
    : order.fulfillmentStatus === "fulfilled"
    ? { label: "Shipped", cls: "bg-emerald-100 text-emerald-700" }
    : order.fulfillmentStatus === "partial"
    ? { label: "Partially Shipped", cls: "bg-amber-100 text-amber-700" }
    : order.financialStatus === "paid"
    ? { label: "Processing", cls: "bg-amber-100 text-amber-700" }
    : { label: order.financialStatus.replace(/_/g, " "), cls: "bg-gray-100 text-gray-600" };

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-extrabold text-gray-900">Order {order.orderNumber}</h2>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${statusBadge.cls}`}>
              {statusBadge.label}
            </span>
          </div>
          <p className="text-sm text-gray-400 mt-0.5">Placed on {formatDate(order.date)}</p>
        </div>
        <button
          onClick={onReset}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line"></i>
          </div>
          Look up another order
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Timeline + Tracking */}
        <div className="lg:col-span-1 space-y-5">
          {/* Timeline card */}
          <div className="border border-gray-100 rounded-xl p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-5">Order Status</h3>
            <div>
              {timeline.map((step, i) => (
                <TimelineStep key={step.label} step={step} isLast={i === timeline.length - 1} />
              ))}
            </div>
          </div>

          {/* Tracking numbers */}
          {order.tracking.length > 0 && (
            <div className="border border-gray-100 rounded-xl p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-2-line text-amber-500"></i>
                </div>
                Tracking
              </h3>
              <div className="space-y-3">
                {order.tracking.map((t) => (
                  <div key={t.number} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-400 mb-1">{t.company}</p>
                    {t.url ? (
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-amber-600 hover:underline break-all cursor-pointer"
                      >
                        {t.number}
                        <i className="ri-external-link-line ml-1 text-xs"></i>
                      </a>
                    ) : (
                      <p className="text-sm font-bold text-gray-900 break-all">{t.number}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shipping address */}
          {order.shippingAddress && (
            <div className="border border-gray-100 rounded-xl p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-4-line text-amber-500"></i>
                </div>
                Ship To
              </h3>
              <div className="text-sm text-gray-600 leading-relaxed">
                <p className="font-semibold text-gray-900">{order.shippingAddress.name}</p>
                <p>{order.shippingAddress.address1}</p>
                <p>
                  {order.shippingAddress.city}, {order.shippingAddress.province} {order.shippingAddress.zip}
                </p>
                <p>{order.shippingAddress.country}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Items + Totals */}
        <div className="lg:col-span-2 space-y-5">
          {/* Line items */}
          <div className="border border-gray-100 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h3 className="text-sm font-bold text-gray-900">
                Items Ordered ({order.items.reduce((s, i) => s + i.quantity, 0)})
              </h3>
            </div>
            <div className="divide-y divide-gray-50">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 px-5 py-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                    <i className="ri-box-3-line text-xl text-amber-400"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 leading-snug">{item.title}</p>
                    {item.variantTitle && item.variantTitle !== "Default Title" && (
                      <p className="text-xs text-gray-400 mt-0.5">{item.variantTitle}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">Qty: {item.quantity}</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900 shrink-0">
                    {formatCurrency(item.price, order.currency)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Order totals */}
          <div className="border border-gray-100 rounded-xl p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Order Summary</h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="font-medium text-gray-900">{formatCurrency(order.subtotal, order.currency)}</span>
              </div>
              {parseFloat(order.totalDiscounts) > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Discounts</span>
                  <span className="font-medium">−{formatCurrency(order.totalDiscounts, order.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span className="font-medium text-emerald-600">Free</span>
              </div>
              {parseFloat(order.totalTax) > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Tax</span>
                  <span className="font-medium text-gray-900">{formatCurrency(order.totalTax, order.currency)}</span>
                </div>
              )}
              <div className="border-t border-gray-100 pt-2.5 flex justify-between font-extrabold text-gray-900 text-base">
                <span>Total</span>
                <span>{formatCurrency(order.total, order.currency)}</span>
              </div>
            </div>
          </div>

          {/* Help CTA */}
          <div className="bg-gray-50 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-gray-900 mb-0.5">Need help with this order?</p>
              <p className="text-xs text-gray-500">Our team is available Mon–Fri 9am–6pm EST</p>
            </div>
            <a
              href="/contact"
              className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 hover:border-amber-400 hover:text-amber-600 font-semibold px-5 py-2.5 rounded-lg transition-all text-sm cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-customer-service-2-line"></i>
              </div>
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
